import { memo, useEffect, useState, useCallback } from 'react';
import Form from '../../components/Form';
import {
    getOwners,
    getLambsByDateRange,
    getLambingsByDateRange,
    addNewLambInFlock,
    updateLambStatus,
} from "../../api";
import { useSnackbar } from '../../utils/index.js';
import { Box, Typography, Button, Modal, Slide } from "@mui/material";
import Spinner from '../../components/Spinner';
import useListStyles from '../../components/styles/List';
import Table from '../../components/Table.js';
import {
    damCodeHeaderNoClick,
    sireCodeHeaderNoClick,
    birthDateHeaderNotFilterable,
    lambHistoricalCodeHeader,
    sexHeaderNoFilter,
    birthWeightHeader,
    weaningWeightHeader,
    lambStatusHeaderClickable,
    lactationNumberHeader,
    prolificacyHeader,
    lambingDateHeader,
    abortionCodeHeader,
    maleAliveHeader,
    maleDeadHeader,
    femaleAliveHeader,
    femaleDeadHeader,
    flockCodeHeader,
    lambSlugCodeHeader,
} from '../../components/TableHeaders.js';


const ManageLambings = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [step, setStep] = useState(0);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [seasonDropdownValues, setSeasonDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [fetchedLambs, setFetchedLambs] = useState([]);
    const [optionSelected, setOptionSelected] = useState(null);
    const [fetchedLambings, setFetchedLambings] = useState([]);
    const [selectedLamb, setSelectedLamb] = useState(null);
    const [isSlideOpen, setIsSlideOpen] = useState(false);
    const [isStatusSlideOpen, setIsStatusSlideOpen] = useState(false);
    const [selectedLambForStatus, setSelectedLambForStatus] = useState(null);

    const classes = useListStyles();
    const { success, error } = useSnackbar();

    const statusDropdownValues = [
        { value: "Παρών", text: "Παρών" },
        { value: "Σφαγή", text: "Σφαγή" },
        { value: "Θάνατος", text: "Θάνατος" },
        { value: "Πώληση", text: "Πώληση" },
        { value: "Άγνωστη", text: "Άγνωστη" },
    ];

    const generateSeasonDropdownValues = useCallback(() => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

        const latestSeasonStartYear = currentMonth >= 8 ? currentYear : currentYear - 1;

        const seasons = [];

        for (let year = 2000; year <= latestSeasonStartYear; year++) {
            const startDate = `1/8/${year}`;
            const endDate = `31/7/${year + 1}`;
            seasons.push({
                value: `${startDate} - ${endDate}`,
                text: `${startDate} - ${endDate}`,
                startYear: year,
                endYear: year + 1
            });
        }

        return seasons.reverse();
    }, []);

    const parseSeasonTodates = useCallback((seasonString) => {
        const match = seasonString.match(/1\/8\/(\d{4}) - 31\/7\/(\d{4})/);
        if (match) {
            const startYear = parseInt(match[1]);
            const endYear = parseInt(match[2]);

            const startDate = new Date(startYear, 7, 1);
            const endDate = new Date(endYear, 6, 31);

            return { startDate, endDate };
        }
        return null;
    }, []);

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));

                const seasons = generateSeasonDropdownValues();
                setSeasonDropdownValues(seasons);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }, [generateSeasonDropdownValues, error]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "season",
            items: seasonDropdownValues,
            placeholder: "Περίοδος Τοκετών",
            defaultValue: seasonDropdownValues[0]?.value || "",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ];

    const addLambsFormContent = [
        {
            customType: "input",
            id: "codeTwo",
            type: "text",
            placeholder: "Κωδικός Ενωτίου Τύπου 2",
            required: true,
            disabled: isSlideOpen ? true : false,
            value: selectedLamb?.displayLambSlug || null,
        },
        {
            customType: "input",
            id: "codeOne",
            type: "text",
            placeholder: "Κωδικός Ενωτίου Τύπου 1",
            required: true,
            value: selectedLamb?.flockCode || null,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ];

    const handleLambSlugClick = (lamb) => {
        setIsSlideOpen(true);
        setSelectedLamb(lamb);
    };

    const handleLambStatusClick = (lamb) => {
        if (lamb.isEndedLamb && lamb.status === "Άγνωστη") {
            setSelectedLamb(lamb);
            setIsSlideOpen(true);
            return;
        }

        setIsStatusSlideOpen(true);
        setSelectedLambForStatus(lamb);
    };

    const lambTableHeaders = [
        damCodeHeaderNoClick,
        birthDateHeaderNotFilterable,
        lambHistoricalCodeHeader,
        sexHeaderNoFilter,
        birthWeightHeader,
        weaningWeightHeader,
        lambStatusHeaderClickable(handleLambStatusClick),
    ];

    const lambingTableHeaders = [
        damCodeHeaderNoClick,
        lambingDateHeader,
        lactationNumberHeader,
        abortionCodeHeader,
        prolificacyHeader,
        maleAliveHeader,
        femaleAliveHeader,
        maleDeadHeader,
        femaleDeadHeader,
        sireCodeHeaderNoClick,
    ];

    const lambsFlockTableHeaders = [
        damCodeHeaderNoClick,
        lambSlugCodeHeader(handleLambSlugClick),
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        flockCodeHeader,
    ];

    const handleSubmitStep0 = async (values) => {
        setIsLoading(true);

        const selOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selOwner);

        const parsedDates = parseSeasonTodates(values.season);
        if (!parsedDates) {
            error("Σφάλμα στην επεξεργασία της επιλεγμένης περιόδου");
            setIsLoading(false);
            return;
        }

        setSelectedStartDate(parsedDates.startDate);
        setSelectedEndDate(parsedDates.endDate);

        setStep(1);
        setIsLoading(false);
    };

    const handleButtonClick = async (option) => {
        setIsLoading(true);
        if (option === 1 || option === 3) {
            const { success: scs, lambs, message } = await getLambsByDateRange(selectedOwner._id, { start: selectedStartDate, end: selectedEndDate });
            if (scs) {
                if (lambs.length === 0) {
                    error("Δεν υπάρχουν αμνοί για την περίοδο που επιλέξατε");
                } else {
                    setFetchedLambs(lambs);
                    setStep(2);
                    setOptionSelected(option);
                }
            } else {
                error(message);
            }
        }
        if (option === 2) {
            const { success: scs, lambings, message } = await getLambingsByDateRange(selectedOwner._id, { start: selectedStartDate, end: selectedEndDate });
            if (scs) {
                if (lambings.length === 0) {
                    error("Δεν υπάρχουν τοκετοί για την περίοδο που επιλέξατε");
                } else {
                    setFetchedLambings(lambings);
                    setStep(2);
                    setOptionSelected(option);
                }
            } else {
                error(message);
            }
        }
        if (option === 4) {
            setStep(2);
            setOptionSelected(option);
        }
        setIsLoading(false);
    };

    const handleLambAdditionSubmit = async (values) => {
        setIsLoading(true);
        values.start = selectedStartDate;
        values.end = selectedEndDate;
        values.lambId = selectedLamb?._id;
        values.codeOne = values.codeOne.trim();
        const { success: scs, message } = await addNewLambInFlock(selectedOwner._id, values);
        if (scs) {
            success(message);
            if (optionSelected === 1 || optionSelected === 3) {
                handleButtonClick(optionSelected);
                setIsSlideOpen(false);
            }
            if (optionSelected === 4) {
                setTimeout(() => {
                    document.getElementById("codeTwo")?.focus();
                }, 100);
            }
        } else {
            error(message);
        }
        setSelectedLamb(null);
        setIsLoading(false);
    };

    const handleLambStatusUpdate = async (values) => {
        setIsLoading(true);
        const { success: scs, message } = await updateLambStatus(
            selectedLambForStatus._id,
            selectedOwner._id,
            { status: values.status }
        );
        if (scs) {
            success(message);
            // Refresh the lambs list
            const { success: fetchScs, lambs, message: fetchMessage } = await getLambsByDateRange(
                selectedOwner._id,
                { start: selectedStartDate, end: selectedEndDate }
            );
            if (fetchScs) {
                setFetchedLambs(lambs);
            } else {
                error(fetchMessage);
            }
            setIsStatusSlideOpen(false);
        } else {
            error(message);
        }
        setSelectedLambForStatus(null);
        setIsLoading(false);
    };

    const updateStatusFormContent = [
        {
            customType: "input",
            id: "lambSlug",
            type: "text",
            placeholder: "Κωδικός Ενωτίου Τύπου 2",
            required: true,
            disabled: true,
            value: selectedLambForStatus?.lambSlug || "",
        },
        {
            customType: "dropdown",
            id: "status",
            items: statusDropdownValues,
            placeholder: "Κατάληξη Αμνού",
            required: true,
            defaultValue: selectedLambForStatus?.status || "",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Ενημέρωση"
        },
    ];

    return (
        <>
            <Spinner open={isLoading} />
            <Modal open={isStatusSlideOpen} onClose={() => {
                setSelectedLambForStatus(null);
                setIsStatusSlideOpen(false);
            }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Slide direction="left" in={isStatusSlideOpen} mountOnEnter unmountOnExit>
                    <Box className={classes.modalContent}>
                        <Typography variant="h5" className={classes.modal_title}>
                            Ενημέρωση Κατάληξης Αμνού
                        </Typography>
                        <Form content={updateStatusFormContent} onSubmit={handleLambStatusUpdate} />
                    </Box>
                </Slide>
            </Modal>
            <Modal open={isSlideOpen} onClose={() => {
                setSelectedLamb(null);
                setIsSlideOpen(false);
            }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Slide direction="left" in={isSlideOpen} mountOnEnter unmountOnExit>
                    <Box className={classes.modalContent}>
                        <Typography variant="h5" className={classes.modal_title}>
                            Καταχώρηση Αμνού στην Εκτροφή
                        </Typography>
                        <Form content={addLambsFormContent} onSubmit={handleLambAdditionSubmit} />
                    </Box>
                </Slide>
            </Modal>
            {step === 0 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και την περίοδο τοκετών που σας ενδιαφέρει:
                        </Typography>
                        <Form
                            content={ownerFormContent}
                            onSubmit={handleSubmitStep0}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 1 && (
                <>
                    <Typography
                        variant="h5"
                        sx={{ margin: "1rem", display: 'flex', justifyContent: 'center', mb: '2rem' }}
                    >
                        {`Διαχείριση τοκετών/αμνών της εκτροφής ${selectedOwner?.lastName} ${selectedOwner?.firstName} για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: '100%', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleButtonClick(1)}
                            sx={{ width: '400px', minHeight: "60px" }}
                        >
                            {`Λίστα αμνών για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleButtonClick(2)}
                            sx={{ width: '400px' }}
                        >
                            {`Λίστα τοκετών για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleButtonClick(3)}
                            sx={{ width: '400px' }}
                        >
                            {`Καταχώρηση αμνών στην εκτροφή για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleButtonClick(4)}
                            sx={{ width: '400px' }}
                        >
                            {`ΝΕΑ καταχώρηση αμνών στην εκτροφή για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => setStep(0)}
                            sx={{ width: '150px', mt: '1rem', color: 'white !important' }}
                        >
                            Πίσω
                        </Button>
                    </Box>
                </>
            )}

            {step === 2 && optionSelected === 1 && (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginTop: "1rem" }}>
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: "100%", marginBottom: "1rem" }}>
                            <Typography variant="h5" className={classes.form_title}>
                                {`Λίστα αμνών για την εκτροφή ${selectedOwner?.lastName} ${selectedOwner?.firstName} για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                            </Typography>
                        </Box>
                        <Table
                            data={fetchedLambs}
                            noDataText={"Δεν βρέθηκαν εγγραφές!"}
                            columns={lambTableHeaders}
                            showPageSizeOptions={true}
                        />
                    </Box>
                </>
            )}
            {step === 2 && optionSelected === 2 && (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginTop: "1rem" }}>
                        <Box display="flex" justifyContent="center" className={classes.form_title} alignItems="center" sx={{ width: "100%", marginBottom: "1rem" }}>
                            <Typography variant="h5" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {`Λίστα τοκετών για την εκτροφή ${selectedOwner?.lastName} ${selectedOwner?.firstName} για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                            </Typography>
                        </Box>
                        <Table
                            data={fetchedLambings}
                            noDataText={"Δεν βρέθηκαν εγγραφές!"}
                            columns={lambingTableHeaders}
                            showPageSizeOptions={true}
                        />
                    </Box>
                </>
            )}
            {step === 2 && optionSelected === 3 && (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", marginTop: "1rem" }}>
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: "100%", marginBottom: "1rem" }}>
                            <Typography variant="h5" className={classes.form_title}>
                                {`Καταχώρηση αμνών στην Εκτροφή ${selectedOwner?.lastName} ${selectedOwner?.firstName} για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                            </Typography>
                        </Box>
                        <Table
                            data={fetchedLambs.filter((lamb) => !lamb.isEndedLamb || lamb.status === "Άγνωστη")}
                            noDataText={"Δεν βρέθηκαν εγγραφές!"}
                            columns={lambsFlockTableHeaders}
                            showPageSizeOptions={true}
                        />
                    </Box>
                </>
            )}
            {step === 2 && optionSelected === 4 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: '9rem' }}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {`Καταχώρηση αμνών στην εκτροφή ${selectedOwner?.lastName} ${selectedOwner?.firstName} για την περίοδο ${new Date(selectedStartDate).toLocaleDateString("el-GR")} - ${new Date(selectedEndDate).toLocaleDateString("el-GR")}`}
                        </Typography>
                        <Form
                            content={addLambsFormContent}
                            onSubmit={handleLambAdditionSubmit}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setStep(1)}
                        sx={{ width: '150px', mt: '1rem', color: 'white !important' }}
                    >
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    );
};

export default memo(ManageLambings);
